import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Box,
  Paper,
  Pagination,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import DataTable from "../../Requirement/components/common/ReusableTable";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import dayjs from "dayjs";
import RemarksThread from "./component/RemarksThread";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import {
  fetchData,
  postQuery,
  searchfetchQueries,
} from "../../../services/BackendServices";
import config from "../../../aws-exports";
import AlertModal from "../../Requirement/components/common/AlerModel";
import { useLoading } from "../../../context/LoadingContext";

const SellerKycComponent = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [kycData, setKycData] = useState([]);

  const bucket = process.env.REACT_APP_AMTPL_BUCKET;
  const bucketfolder = "public/media/amtpl";
  const filePath = `${bucketfolder}/`;
  const fileUrl = `https://${bucket}.s3.${config.aws_project_region}.amazonaws.com/${filePath}`;

  const [searchValues, setSearchValues] = useState({
    idNumber: "",
    status: "",
  });
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [updatedStatus, setUpdatedStatus] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const { setLoading } = useLoading();
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [activeUser, setActiveUser] = useState("");
  const [activeUserName, setActiveUserName] = useState("");

  // Image modal states
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const statusOptions = ["Pending", "Approved"];

  // Helper function to parse KycData string
  const parseKycDataString = (kycDataStr) => {
    if (!kycDataStr) return null;
    try {
      const cleanStr = kycDataStr.replace(/^\{|\}$/g, "");
      const pairs = cleanStr.split(", ");
      const result = {};
      pairs.forEach((pair) => {
        const [key, value] = pair.split("=");
        if (key && value) {
          result[key.trim()] = value.trim();
        }
      });
      return {
        aadhaarVerified: result.stg_aadhaar === "verified",
        aadhaarNumber: result.aadhaar_number || "",
        aadhaarStatus: result.stg_aadhaar || "no",
        panVerified: result.stg_pan === "verified",
        panNumber: result.pan_number || "",
        panStatus: result.stg_pan || "no",
        bankVerified: result.stg_bank === "verified",
        bankAccount: result.bank_account || "",
        bankIfsc: result.bank_ifsc || "",
        bankStatus: result.stg_bank || "no",
      };
    } catch (e) {
      console.error("Error parsing KycData string:", e);
      return null;
    }
  };

  // Fetch KYC data from API
  const fetchKycData = useCallback(async () => {
    setError(null);
    try {
      setLoading(true);
      const userKycs = await fetchData({
        target: "listUserKYCAdmin",
        userOrder: "CreatedAt DESC",
        rowStart,
        pageSize,
        filterTerms: {
          UserType: "SellerFarmer",
          Status: searchValues.status || "",
        },
      });

      const fetchedData = userKycs.items || [];
      const sellerData = fetchedData.filter(
        (item) => item.UserType === "SellerFarmer"
      );

      const modifiedItems = sellerData.map((item) => ({
        ...item,
        id: item.UserKycID,
        parsedKycData: parseKycDataString(item.KycData),
      }));

      setTotalItems(userKycs.total || fetchedData.length);
      setKycData(fetchedData);
      setFilteredData(modifiedItems);
    } catch (err) {
      console.error("Error fetching KYC data:", err);
      setError("Failed to fetch KYC data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [rowStart, pageSize, searchValues, setLoading]);

  const fields = [
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: statusOptions.map((status) => ({
        value: status,
        label: status,
      })),
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, status: value })),
      value: searchValues.status,
    },
  ];

  const handleSearch = useCallback((newSearchValues) => {
    setSearchValues(newSearchValues);
    setRowStart(0);
  }, []);

  const handlePageChange = (event, newPage) => {
    const newRowStart = (newPage - 1) * pageSize;
    setRowStart(newRowStart);
  };

  const handleRowClick = (row) => {
    const parsedKycData = parseKycDataString(row.KycData);
    const detailedRow = {
      id: row.UserKycID,
      emailId: row.EmailID,
      status: row.Status,
      createdAt: row.CreatedAt,
      selfiePhoto: row.LiveSelfiePhoto,
      kycData: parsedKycData,
      rawKycData: row.KycData,
    };
    setSelectedRow(detailedRow);
    setUpdatedStatus(row.Status);
    setActiveTab(0);
  };

  const handleCloseReview = () => {
    setSelectedRow(null);
    setUpdatedStatus("");
    setActiveTab(0);
  };

  const handleUpdateStatus = async () => {
    if (!selectedRow || updatedStatus === selectedRow.status) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const userKycInput = {
        UserKycID: selectedRow.id,
        EmailID: selectedRow.emailId,
        Status: updatedStatus,
        IdType: "",
        IdNumber: "",
      };

      await postQuery({
        target: "updateUserKyc",
        userKycInput,
      });

      selectedRow.status = updatedStatus;
      fetchKycData();

      setModal({
        show: true,
        type: "success",
        title: "Success!",
        message: "KYC status updated successfully.",
        autoClose: false,
      });
    } catch (err) {
      console.error("Error updating KYC status:", err);
      setError("Failed to update status. Please try again.");
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: err.message || "Failed to update status. Please try again.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    let bgColor = "",
      textColor = "";
    switch (status) {
      case "Pending":
        bgColor = "#fef3c7";
        textColor = "#b45309";
        break;
      case "Approved":
        bgColor = "#d1fae5";
        textColor = "#065f46";
        break;
      case "Verified":
        bgColor = "#d1fae5";
        textColor = "#065f46";
        break;
      default:
        bgColor = "#f3f4f6";
        textColor = "#374151";
    }
    return (
      <Box
        sx={{
          backgroundColor: bgColor,
          color: textColor,
          padding: "4px 10px",
          borderRadius: "999px",
          fontSize: "0.75rem",
          fontWeight: 500,
          display: "inline-block",
        }}
      >
        {status === "Pending" ? "In Review" : status}
      </Box>
    );
  };

  const getVerificationBadge = (verified) => {
    if (verified) {
      return (
        <span
          style={{
            backgroundColor: "#dcfce7",
            color: "#166534",
            padding: "4px 12px",
            borderRadius: "5px",
            fontSize: "12px",
            fontWeight: 500,
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 14 }} />
          Verified
        </span>
      );
    }
    return (
      <span
        style={{
          backgroundColor: "#fee2e2",
          color: "#991b1b",
          padding: "4px 12px",
          borderRadius: "5px",
          fontSize: "12px",
          fontWeight: 500,
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <CancelIcon sx={{ fontSize: 14 }} />
        Not Verified
      </span>
    );
  };

  const paginatedData = filteredData.slice(rowStart, rowStart + pageSize);

  const columns = [
    { id: "EmailID", numeric: false, label: "Email" },
    {
      id: "CreatedAt",
      numeric: false,
      label: "Submitted",
      renderCell: (row) =>
        row.CreatedAt ? dayjs(row.CreatedAt).format("DD-MM-YYYY") : "",
    },
    {
      id: "Status",
      numeric: false,
      label: "Status",
      renderCell: (row) => getStatusBadge(row.Status),
    },
  ];

  const pageCount = Math.ceil(totalItems / pageSize);
  const currentPage = Math.floor(rowStart / pageSize) + 1;

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_ADMINS")) {
          await Auth.signOut();
          navigate("/");
        } else {
          const email = user.attributes.email;
          const usrname = user.attributes.given_name;
          setActiveUser(email);
          setActiveUserName(usrname);
        }
      } catch (error) {
        await Auth.signOut();
        navigate("/");
      }
    };

    checkUser();
  }, [navigate]);

  useEffect(() => {
    fetchKycData();
  }, [fetchKycData, searchValues]);

  const downloadCSV = async () => {
    setLoading(true);
    try {
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const fileExtension = ".xlsx";
      let dataToExport = [];
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listUserKYCAdmin",
          userOrder: "UserKycID",
          rowStart: 0,
          pageSize: 1,
          filterTerms: { UserType: "SellerFarmer" },
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allKybData] = await Promise.all([
          searchfetchQueries({
            target: "listUserKYCAdmin",
            userOrder: "UserKycID",
            rowStart: 0,
            pageSize: totalCount,
            filterTerms: { UserType: "SellerFarmer" },
          }),
        ]);
        dataToExport = allKybData;
      }

      let fileName = `Seller_KYC_Data_${dayjs().format("YYYYMMDD")}`;
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
    } catch (error) {
      console.error("Error loading data: ", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <style>{`
        .form-check-label { margin-left: 8px; }
        .form-check-input:checked { background-color: #ff9800; border-color: #ff9800; }
        .form-check-input:focus { border-color: #ff9800; box-shadow: 0 0 0 0.2rem rgba(255, 152, 0, 0.25); }
        .verification-card { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
        .verification-card-title { font-weight: 600; color: #374151; margin-bottom: 12px; font-size: 14px; }
        .verification-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
        .verification-row:last-child { border-bottom: none; }
        .verification-label { color: #6b7280; font-size: 13px; }
        .verification-value { font-weight: 500; color: #111827; font-size: 13px; }
      `}</style>
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <Paper elevation={2} sx={{ p: 3 }}>
        <div className="search-filter4">
          <div className="title4">
            <div className="head-text">Seller KYC</div>
          </div>
          <div className="filter4">
            <SearchFilter fields={fields} onSearch={handleSearch} />
          </div>
          <div className="download-csv">
            <button className="submit-btn-more" onClick={downloadCSV}>
              Download Data(XLSX)
            </button>
          </div>
        </div>

        <div className="table-content6">
          <DataTable
            columns={columns}
            data={paginatedData}
            selectedRows={selectedRow ? [selectedRow.id] : []}
            onRowClick={handleRowClick}
            initialOrderBy="CreatedAt"
          />
        </div>
        <div className="pagination mt-6 flex justify-center">
          <Pagination
            count={pageCount}
            page={currentPage}
            onChange={handlePageChange}
            sx={{
              marginBottom: "10px",
              "& .MuiPaginationItem-root": {
                backgroundColor: "#f0f0f0",
                "&.Mui-selected": {
                  backgroundColor: "#de6b14",
                  color: "#fff",
                },
                "&:hover": {
                  backgroundColor: "#e0e0e0",
                  color: "#de6b14",
                },
              },
            }}
          />
        </div>
      </Paper>

      {selectedRow && (
        <Paper elevation={2} sx={{ p: 4, mt: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <div className="review-text">
              <b>Seller KYC Review:</b> {selectedRow.emailId}
            </div>
            <div className="d-flex gap-2 align-items-center">
              <button className="submit-btn-more" onClick={handleUpdateStatus}>
                Submit
              </button>
              <button className="submit-btn-more" onClick={handleCloseReview}>
                Cancel
              </button>
            </div>
          </Box>

          <div className="container-fluid">
            <div className="row mb-3">
              <div className="col-12">
                <div
                  className="d-flex justify-content-between align-items-center p-2"
                  style={{
                    backgroundColor:
                      selectedRow.status === "Approved"
                        ? "#d1fae573"
                        : "#fef3c750",
                    color:
                      selectedRow.status === "Approved" ? "#065f46" : "#b45309",
                    borderLeft:
                      selectedRow.status === "Approved"
                        ? "3px solid #00b658fb"
                        : "3px solid #b69203f7",
                    borderRadius: "5px",
                  }}
                >
                  <span
                    className="p-1 px-2 rounded"
                    style={{ fontSize: "0.875rem" }}
                  >
                    Current Status: {getStatusBadge(selectedRow.status)}
                  </span>
                </div>
              </div>
            </div>

            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{ mb: 2, "& .MuiTabs-indicator": { display: "none" } }}
            >
              <Tab
                label="KYC Verification Information"
                sx={{
                  borderBottom: "2px solid #de6b14 !important",
                  "&:not(.Mui-selected)": {
                    borderBottom: "none",
                    border: "none",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#de6b14 !important",
                    color: "white",
                    borderBottom: "2px solid #de6b14 !important",
                    border: "none",
                  },
                }}
              />
              <Tab
                label="Remarks"
                disabled={selectedRow.status === "Approved"}
                sx={{
                  borderBottom: "2px solid #de6b14 !important",
                  "&:not(.Mui-selected)": {
                    borderBottom: "none",
                    border: "none",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#de6b14 !important",
                    color: "white",
                    borderBottom: "2px solid #de6b14 !important",
                    border: "none",
                  },
                }}
              />
            </Tabs>

            {activeTab === 0 && (
              <Box className="tab-content">
                <div className="card p-3">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label
                        className="form-label"
                        style={{ fontWeight: 500, color: "#666" }}
                      >
                        Status
                      </label>
                      <select
                        value={updatedStatus}
                        onChange={(e) => setUpdatedStatus(e.target.value)}
                        className="form-select"
                      >
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                      </select>
                    </div>
                  </div>

                  {/* Aadhaar Verification Card */}
                  <div className="verification-card">
                    <div className="verification-card-title">
                      Aadhaar Verification
                    </div>
                    <div className="verification-row">
                      <span className="verification-label">Status</span>
                      <span className="verification-value">
                        {getVerificationBadge(
                          selectedRow.kycData?.aadhaarVerified
                        )}
                      </span>
                    </div>
                    {selectedRow.kycData?.aadhaarNumber && (
                      <div className="verification-row">
                        <span className="verification-label">
                          Aadhaar Number
                        </span>
                        <span className="verification-value">
                          XXXX-XXXX-
                          {selectedRow.kycData.aadhaarNumber.slice(-4)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* PAN Verification Card */}
                  <div className="verification-card">
                    <div className="verification-card-title">
                      PAN Verification
                    </div>
                    <div className="verification-row">
                      <span className="verification-label">Status</span>
                      <span className="verification-value">
                        {getVerificationBadge(selectedRow.kycData?.panVerified)}
                      </span>
                    </div>
                    {selectedRow.kycData?.panNumber && (
                      <div className="verification-row">
                        <span className="verification-label">PAN Number</span>
                        <span className="verification-value">
                          {selectedRow.kycData.panNumber}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Bank Verification Card */}
                  <div className="verification-card">
                    <div className="verification-card-title">
                      Bank Account Verification
                    </div>
                    <div className="verification-row">
                      <span className="verification-label">Status</span>
                      <span className="verification-value">
                        {getVerificationBadge(
                          selectedRow.kycData?.bankVerified
                        )}
                      </span>
                    </div>
                    {selectedRow.kycData?.bankAccount && (
                      <div className="verification-row">
                        <span className="verification-label">
                          Account Number
                        </span>
                        <span className="verification-value">
                          XXXX-XXXX-{selectedRow.kycData.bankAccount.slice(-4)}
                        </span>
                      </div>
                    )}
                    {selectedRow.kycData?.bankIfsc && (
                      <div className="verification-row">
                        <span className="verification-label">IFSC Code</span>
                        <span className="verification-value">
                          {selectedRow.kycData.bankIfsc}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Selfie Photo */}
                  {selectedRow.selfiePhoto && (
                    <div className="verification-card">
                      <div className="verification-card-title">
                        Live Photo / Selfie
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <Tooltip title="Click to view full image" arrow>
                          <img
                            src={`${fileUrl}${selectedRow.selfiePhoto}`}
                            alt="Selfie"
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              cursor: "pointer",
                              border: "1px solid #e5e7eb",
                            }}
                            onClick={() => {
                              setSelectedImageUrl(
                                `${fileUrl}${selectedRow.selfiePhoto}`
                              );
                              setImageModalOpen(true);
                            }}
                          />
                        </Tooltip>
                        <span style={{ color: "#6b7280", fontSize: "12px" }}>
                          Click image to view full size
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </Box>
            )}

            {activeTab === 1 && (
              <Box className="tab-content">
                <RemarksThread
                  kycId={selectedRow.id}
                  currentUserId={activeUser}
                  currentUserName={activeUserName}
                />
              </Box>
            )}
          </div>
        </Paper>
      )}

      {/* Image Modal */}
      {imageModalOpen && (
        <Dialog
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          maxWidth={false}
          sx={{
            "& .MuiDialog-paper": {
              width: "450px",
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle sx={{ m: 0, p: 2 }}>
            <IconButton
              aria-label="close"
              onClick={() => setImageModalOpen(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{ p: 2, display: "flex", justifyContent: "center" }}
          >
            <img
              src={selectedImageUrl}
              alt="Full Size Selfie"
              style={{
                width: "400px",
                height: "400px",
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default SellerKycComponent;
